import React, { useEffect, useMemo, useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Input } from 'src/components/Input';
import { PasswordInput } from 'src/components/PasswordInput';
import { Select } from 'src/components/Select';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { AdminUserRecord } from 'src/api/adminManagement';
import { AdminRoleRecord, ADMIN_USER_STATUSES } from 'src/utils/rbacCatalog';
import { resolveRoleSelectValue } from 'src/api/adminUserUtils';
import {
  getAdminEmailError,
  getAdminPasswordError,
  isValidAdminEmail,
  isValidAdminPassword,
} from 'src/utils/adminUserValidation';
import { FieldError, FormField, FormGrid } from '../styled';

export type AdminUserCreateFormPayload = {
  email: string;
  password: string;
  role_id: string;
};

export type AdminUserUpdateFormPayload = {
  role_id: string;
  status: string;
};

type AdminUserFormModalProps = {
  admin?: AdminUserRecord | null;
  roles: AdminRoleRecord[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onCreate: (payload: AdminUserCreateFormPayload) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  onUpdate: (payload: AdminUserUpdateFormPayload) => Promise<void>;
};

const AdminUserFormModal: React.FC<AdminUserFormModalProps> = ({
  admin,
  roles,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const isEdit = !!admin;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState('active');
  const [initialRoleId, setInitialRoleId] = useState('');
  const [initialStatus, setInitialStatus] = useState('active');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admin) {
      const resolvedRoleId = resolveRoleSelectValue(admin, roles);
      const resolvedStatus = admin.status ?? 'active';
      setRoleId(resolvedRoleId);
      setStatus(resolvedStatus);
      setInitialRoleId(resolvedRoleId);
      setInitialStatus(resolvedStatus);
      return;
    }

    setEmail('');
    setPassword('');
    setRoleId('');
    setStatus('active');
    setInitialRoleId('');
    setInitialStatus('active');
  }, [admin, roles]);

  const roleOptions = roles.map((r) => ({
    value: r._id ?? r.key,
    label: r.name,
  }));

  const trimmedEmail = email.trim();
  const emailError = getAdminEmailError(email);
  const passwordError = getAdminPasswordError(password);
  const emailInvalid = trimmedEmail.length > 0 && emailError !== null;
  const passwordInvalid = password.length > 0 && passwordError !== null;
  const emailValid = isValidAdminEmail(email);
  const passwordValid = isValidAdminPassword(password);

  const hasChanges = roleId !== initialRoleId || status !== initialStatus;
  const canSave = useMemo(() => {
    if (isEdit) return Boolean(roleId && hasChanges);
    return Boolean(emailValid && passwordValid && roleId);
  }, [isEdit, emailValid, passwordValid, roleId, hasChanges]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await onUpdate({ role_id: roleId, status });
      } else {
        await onCreate({
          email: trimmedEmail,
          password,
          role_id: roleId,
        });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const displayEmail = admin?.email ?? admin?.user?.email ?? '—';

  const actionButtons = (
    <Container display="flex" gap="0.75rem" justifyContent="flex-end" width="100%">
      <Button text="Cancel" className="outline" onClick={onClose} />
      <Button
        text={saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create User'}
        className="black"
        disabled={!canSave || saving}
        onClick={handleSubmit}
      />
    </Container>
  );

  return (
    <Modal isOpen onClose={onClose} footer={actionButtons}>
      <Container fontWeight={600} fontSize="1.25rem" paddingBottom="1rem">
        {isEdit ? 'Edit User' : 'Create User'}
      </Container>
      <FormGrid>
        {isEdit ? (
          <Input
            label="Email"
            type="email"
            value={displayEmail === '—' ? '' : displayEmail}
            readonly
            disabled
          />
        ) : (
          <>
            <FormField>
              <Input
                label="Email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailInvalid && emailError && <FieldError>{emailError}</FieldError>}
            </FormField>
            <FormField>
              <PasswordInput
                label="Password"
                placeholder="Set initial password"
                value={password}
                onChange={setPassword}
              />
              {passwordInvalid && passwordError && <FieldError>{passwordError}</FieldError>}
            </FormField>
          </>
        )}
        <Select
          label="Role"
          value={roleId}
          options={roleOptions}
          onChange={(e) => setRoleId(e.target.value)}
          placeholder="Select a role"
        />
        {isEdit && (
          <Select
            label="Status"
            value={status}
            options={ADMIN_USER_STATUSES.map((s) => ({ value: s, label: s }))}
            onChange={(e) => setStatus(e.target.value)}
          />
        )}
      </FormGrid>
    </Modal>
  );
};

export default AdminUserFormModal;
